import { SDKStatusCode } from "@nebula-sdk/core";
import type {
  WEBSDKAccountAuthorizeInfoModel, WEBSDKAccountAuthorizeUrlModel, WEBSDKAccountGenerateCaptchaModel, WEBSDKAccountPlayerInfoModel,
  WEBSDKAccountSendCodeModel, WEBSDKAccountUserInfoModel, WEBSDKAccountVerifyCodeModel,
} from "./model";
import type {
  WEBSDKAccountAuthorize, WEBSDKAccountAuthorizeInfoArgs, WEBSDKAccountAuthorizeInfoResult, WEBSDKAccountAuthorizeUrlResult,
  WEBSDKAccountCountdownListener, WEBSDKAccountGenerateCaptchaArgs, WEBSDKAccountGenerateCaptchaResult,
  WEBSDKAccountRequestPlayerInfoArgs, WEBSDKAccountRequestPlayerResult, WEBSDKAccountSendCodeArgs, WEBSDKAccountSendCodeResult,
  WEBSDKAccountSendVerifyCodeArgs, WEBSDKAccountSendVerifyCodeResult, WEBSDKAccountState, WEBSDKAccountUserInfoResult,
  WEBSDKAccountVerifyCaptchaArgs, WEBSDKAccountVerifyCaptchaResult, WEBSDKAccountVerifyCodeArgs, WEBSDKAccountVerifyCodeResult,
  WEBSDKAccountVerifyInputCodeArgs, WEBSDKAccountVerifyInputCodeResult,
} from "./type";
import type { NebulaWEBGameSDKCore } from "../core";
import type { SDKResult } from "@nebula-sdk/core";

// 账号服务
export class NebulaWEBGameSDKAccount {
  // 构造函数
  public constructor(core: NebulaWEBGameSDKCore) {
    this.core = core;
  }

  // 核心服务
  private readonly core: NebulaWEBGameSDKCore;

  // 内部参数
  private readonly state: WEBSDKAccountState = {
    countdownTimer: -1,
    countdownTimestamp: 0,
    countdownSeconds: 0,
    countdownListener: null,
    wechatAuthorizeKey: "NEBULA_SDK_WEB_ACCOUNT_AUTHORIZE",
  };

  // 微信授权信息
  public get authorize(): WEBSDKAccountAuthorize | null {
    const authorize = this.core.localStorage.getItem(this.state.wechatAuthorizeKey);
    return authorize ? (authorize as WEBSDKAccountAuthorize) : null;
  }

  // 微信授权信息
  public set authorize(value: WEBSDKAccountAuthorize | null) {
    this.core.localStorage.setItem(this.state.wechatAuthorizeKey, value);
  }

  // 获取微信授权地址
  public async requestAuthorizeUrl(): Promise<WEBSDKAccountAuthorizeUrlResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountAuthorizeUrlModel>({
      url: "/passport/web/wechat/getAuthorizeUrl",
      method: "GET",
      data: { url: window.location.href, state: this.core.options!.config.appId },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "获取微信授权地址失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestAuthorizeUrl",
        eventName: "获取微信授权地址失败",
        eventGroup: "account",
        eventValue: { args: null, result },
        eventLevel: "error",
      });
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "获取微信授权地址失败",
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestAuthorizeUrl",
      eventName: "获取微信授权地址成功",
      eventGroup: "account",
      eventValue: { args: null, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取微信授权地址成功",
      data: { url: result.data.url },
    };
  }

  // 获取微信授权信息
  public async requestAuthorizeInfo(
    args: WEBSDKAccountAuthorizeInfoArgs,
  ): Promise<WEBSDKAccountAuthorizeInfoResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountAuthorizeInfoModel>({
      url: "/passport/web/wechat/authorize",
      method: "GET",
      data: { code: args.code, app_id: args.appId },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "获取微信授权信息失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestAuthorizeInfo",
        eventName: "获取微信授权信息失败",
        eventGroup: "account",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "获取微信授权信息失败",
        data: null,
      };
    }

    const authorize = {
      appId: result.data.wxpay_app_id,
      openId: result.data.wxpay_open_id,
    };
    this.authorize = authorize;

    void this.core.scopeRequestTrack({
      eventId: "requestAuthorizeInfo",
      eventName: "获取微信授权信息成功",
      eventGroup: "account",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取微信授权信息成功",
      data: authorize,
    };
  }

  // 获取角色信息
  public async requestPlayerInfo(
    args: WEBSDKAccountRequestPlayerInfoArgs,
  ): Promise<WEBSDKAccountRequestPlayerResult> {
    const result = await this.core.scopeRequestPassport<Array<WEBSDKAccountPlayerInfoModel>>({
      url: "/passport/web/user/playerList",
      method: "GET",
      data: { app_id: this.core.options?.config.appId, token: this.core.userInfo?.token },
    });

    if (result.status === "error") {
      this.core.log({ code: SDKStatusCode.Error, msg: "获取玩家信息失败", data: result });
      void this.core.scopeRequestTrack({
        eventId: "requestPlayerInfo",
        eventName: "获取玩家信息失败",
        eventGroup: "account",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "获取玩家信息失败", data: null };
    }

    const playerInfo = result.data?.find((player) => {
      return player.server_id === args.serverId && player.pid === args.playerId;
    });
    if (!playerInfo) {
      void this.core.scopeRequestTrack({
        eventId: "requestPlayerInfo",
        eventName: "获取玩家信息成功",
        eventGroup: "account",
        eventValue: { args, result },
        eventLevel: "info",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "玩家信息不存在", data: null };
    }

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取玩家信息成功",
      data: {
        channelId: playerInfo.channel_id,
        channelName: playerInfo.channel_name,
        serverId: playerInfo.server_id,
        serverName: playerInfo.server_name,
        playerId: playerInfo.pid,
        playerName: playerInfo.pname,
        level: playerInfo.level,
        vipLevel: playerInfo.vip_level,
        lastLoginTime: playerInfo.last_login_time,
      },
    };
  }

  // 生成图形验证码
  public async requestGenerateCaptcha(
    args: WEBSDKAccountGenerateCaptchaArgs,
  ): Promise<WEBSDKAccountGenerateCaptchaResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountGenerateCaptchaModel>({
      url: "/passport/web/vcode/getCaptcha",
      method: "GET",
      data: { app_id: this.core.options?.config.appId, mobile: args.mobile },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.GenerateCaptchaError,
        msg: "获取图形验证码失败",
        data: result,
      });
      return {
        status: "error",
        code: SDKStatusCode.GenerateCaptchaError,
        msg: result.msg,
        data: null,
      };
    }
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取图形验证码成功",
      data: { captcha: `data:image/png;base64,${result.data.captcha}` },
    };
  }

  // 验证图形验证码
  public async requestVerifyCaptcha(
    args: WEBSDKAccountVerifyCaptchaArgs,
  ): Promise<WEBSDKAccountVerifyCaptchaResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountVerifyCodeModel>({
      url: "/passport/web/vcode/checkCaptcha",
      method: "POST",
      data: { app_id: this.core.options?.config.appId, mobile: args.mobile, phrase: args.code },
    });

    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.GenerateCaptchaError,
        msg: "校验图形验证码失败",
        data: result,
      });
      return {
        status: "error",
        code: SDKStatusCode.GenerateCaptchaError,
        msg: result.msg,
        data: null,
      };
    }

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "校验图形验证码成功",
      data: null,
    };
  }

  // 发送短信验证码
  public async requestSendCode(
    args: WEBSDKAccountSendCodeArgs,
  ): Promise<WEBSDKAccountSendCodeResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountSendCodeModel>({
      url: "/passport/web/vcode/send",
      method: "POST",
      data: { app_id: this.core.options?.config.appId, mobile: args.mobile },
    });

    if (result.status === "error") {
      const error: Record<string, SDKStatusCode> = {
        3101630: SDKStatusCode.SendVerifyLimitError,
      };

      this.core.log({
        code: error[result.code] ?? SDKStatusCode.SendVerifyCodeError,
        msg: "发送短信验证码失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestSendCode",
        eventName: "发送短信验证码失败",
        eventGroup: "account",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return {
        status: "error",
        code: error[result.code] ?? SDKStatusCode.SendVerifyCodeError,
        msg: result.msg,
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestSendCode",
      eventName: "发送短信验证码成功",
      eventGroup: "account",
      eventValue: { args: { mobile: args.mobile }, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "发送短信验证码成功",
      data: { seconds: result.data.countdown_seconds },
    };
  }

  // 校验验证码
  private async requestVerifyCode(
    args: WEBSDKAccountVerifyCodeArgs,
  ): Promise<WEBSDKAccountVerifyCodeResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKAccountVerifyCodeModel>({
      url: "/passport/web/vcode/verify",
      method: "POST",
      data: { app_id: this.core.options?.config.appId, mobile: args.mobile, vcode: args.code },
    });

    const error: Record<string, SDKStatusCode> = {
      3101611: SDKStatusCode.LoginFreezeError,
    };

    if (result.status === "error") {
      this.core.log({
        code: error[result.code] ?? SDKStatusCode.VerifyInputCodeError,
        msg: "校验验证码失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestVerifyCode",
        eventName: "校验验证码失败",
        eventGroup: "account",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return {
        status: "error",
        code: error[result.code] ?? SDKStatusCode.VerifyInputCodeError,
        msg: result.msg,
        data: null,
      };
    }
    void this.core.scopeRequestTrack({
      eventId: "requestVerifyCode",
      eventName: "校验验证码成功",
      eventGroup: "account",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "校验验证码成功",
      data: { token: result.data.token },
    };
  }

  // 获取用户信息
  public async requestUserInfo(): Promise<WEBSDKAccountUserInfoResult> {
    if (!this.core.userInfo?.token) {
      return { status: "error", code: SDKStatusCode.NoLogin, msg: "用户未登录", data: null };
    }
    const result = await this.core.scopeRequestPassport<WEBSDKAccountUserInfoModel>({
      url: "/passport/web/user/info",
      method: "GET",
      data: { app_id: this.core.options?.config.appId, token: this.core.userInfo.token },
    });

    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.GetUserInfoError,
        msg: "获取用户信息失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestUserInfo",
        eventName: "获取用户信息失败",
        eventGroup: "account",
        eventValue: { args: null, result },
        eventLevel: "error",
      });
      this.core.userInfo = null;
      return {
        status: "error",
        code: SDKStatusCode.GetUserInfoError,
        msg: result.msg,
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestUserInfo",
      eventName: "获取用户信息成功",
      eventGroup: "account",
      eventValue: { args: null, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取用户信息成功",
      data: {
        unionId: result.data.union_id,
        openId: result.data.open_id,
        mobile: result.data.mobile,
      },
    };
  }

  // 发送短信验证码
  public async sendVerifyCode(
    args: WEBSDKAccountSendVerifyCodeArgs,
  ): Promise<WEBSDKAccountSendVerifyCodeResult> {
    const result = await this.requestSendCode(args);

    if (result.status === "success") {
      this.state.countdownTimestamp = Date.now() + result.data!.seconds * 1000;
    } else {
      this.state.countdownTimestamp = 0;
    }

    if (this.state.countdownListener) {
      this.onCountdownChange(this.state.countdownListener);
    }

    result.data = null;
    return result as WEBSDKAccountSendVerifyCodeResult;
  }

  // 验证输入验证码
  public async verifyInputCode(
    args: WEBSDKAccountVerifyInputCodeArgs,
  ): Promise<WEBSDKAccountVerifyInputCodeResult> {
    const verifyResult = await this.requestVerifyCode(args);
    if (verifyResult.status === "error") {
      this.core.userInfo = null;
      return verifyResult as WEBSDKAccountVerifyInputCodeResult;
    }
    this.core.userInfo = {
      openId: "",
      unionId: "",
      token: verifyResult.data!.token,
      scene: "",
      nbOpenId: "",
      nbUnionId: "",
      firstLogin: false,
      firstRegisterTime: 0,
    };

    const userInfoResult = await this.requestUserInfo();
    if (userInfoResult.status === "error") {
      this.core.userInfo = null;
      return userInfoResult as WEBSDKAccountVerifyInputCodeResult;
    }
    this.core.userInfo = {
      openId: userInfoResult.data!.openId,
      unionId: String(userInfoResult.data!.unionId),
      token: verifyResult.data!.token,
      scene: "",
      nbOpenId: userInfoResult.data!.openId,
      nbUnionId: String(userInfoResult.data!.unionId),
      firstLogin: false,
      firstRegisterTime: 0,
    };
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "验证输入验证码成功",
      data: this.core.userInfo,
    };
  }

  // 绑定倒计时变更事件
  public onCountdownChange(countdownCallback: WEBSDKAccountCountdownListener): void {
    window.clearInterval(this.state.countdownTimer);
    this.state.countdownTimer = -1;
    this.state.countdownListener = countdownCallback;

    const diffSeconds = Math.floor((this.state.countdownTimestamp - Date.now()) / 1000);
    this.state.countdownSeconds = diffSeconds < 0 ? 0 : diffSeconds;
    this.state.countdownListener(this.state.countdownSeconds);

    if (this.state.countdownSeconds === 0) return;
    this.state.countdownTimer = window.setInterval(() => {
      this.state.countdownSeconds -= 1;
      this.state.countdownListener?.(this.state.countdownSeconds);
      if (this.state.countdownSeconds > 0) return;
      window.clearInterval(this.state.countdownTimer);
      this.state.countdownTimer = -1;
    }, 1000);
  }

  // 解绑倒计时变更事件
  public offCountdownChange(): void {
    window.clearInterval(this.state.countdownTimer);
    this.state.countdownTimer = -1;
    this.state.countdownListener = null;
  }

  // 打开用户协议
  public async openUserContract(): Promise<SDKResult<null>> {
    window.open(
      "https://legal.737.com/policy/passport-privacy.html",
      "_blank",
      "noopener,noreferrer",
    );
    void this.core.scopeRequestTrack({
      eventId: "openUserContract",
      eventName: "打开用户协议",
      eventGroup: "account",
      eventValue: { url: "https://legal.737.com/policy/passport-privacy.html" },
      eventLevel: "info",
    });
    return await Promise.resolve({
      status: "success",
      code: SDKStatusCode.Success,
      msg: "打开用户协议成功",
      data: null,
    });
  }

  // 打开隐私政策
  public async openPrivacyContract(): Promise<SDKResult<null>> {
    window.open(
      "https://legal.737.com/policy/passport-privacy.html",
      "_blank",
      "noopener,noreferrer",
    );
    void this.core.scopeRequestTrack({
      eventId: "openPrivacyContract",
      eventName: "打开隐私政策",
      eventGroup: "account",
      eventValue: { url: "https://legal.737.com/policy/passport-privacy.html" },
      eventLevel: "info",
    });

    return Promise.resolve({
      status: "success",
      code: SDKStatusCode.Success,
      msg: "打开隐私政策成功",
      data: null,
    });
  }
}
